import style from "./style.module.css";
import { useHistory, useLocation } from "react-router";
import { CheckOutlined, EllipsisOutlined } from "@ant-design/icons";
import { Alert, Button, Dropdown, MenuProps, Space, Spin, Table, Tooltip, Col, Row } from "antd";
import { ColumnGroupType, ColumnType, TablePaginationConfig } from "antd/lib/table";
import { ApiURL, AppURL } from "utils/Actions";
import api from "ApiClient";
import ErrorToDisplay from "components/error-handling/ErrorToDisplay";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  sortTable,
  searchTable,
  filterTable,
  applyCriteria
} from "redux/reducers/SRSApplicantSlice";
import { API_RESPONSE_STATUS, TABLE_TYPE, SCHOOL_RATED, TABLE_FILTER } from "utils/Constants";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Search from "antd/lib/input/Search";
import emptyBox from "assets/images/empty-box.svg";
import emptyBoxTransparent from "assets/images/empty-box-transparent.svg";
import TableFooter from "components/common/table-footer";
import { schoolRatedOpt, studentTableSize } from "utils/AssetsData";
import { capitalize, isEmpty } from "lodash";
import { FilterValue } from "antd/lib/table/interface";
import { PaginationPropType, SRSApplicantType } from "utils/Types";
import iconSearch from "assets/icons/icon-search.svg";
import { removeRatedStudent } from "redux/reducers/RatingsSlice";
import checkCircle from "assets/icons/notification/check-circle.svg";
import {
  getStatusAndStyling,
  getTextForShowTotal,
  hasFilterChange,
  updateFilters
} from "utils/hooks/studentTableActions";
import { AccessibleSortMsg } from "components/common/accessible-sort-msg";
import { backToDashboard, createStatusFilter, setSorter, startRating } from "./actions";
import { formatName } from "utils/Format";
import FilterBox from "components/common/filterBox";
import FlagModal from "components/common/flag-modal";
import { fetchSRSApplicants } from "redux/reducers/SRSApplicantSlice";
import CSVLinkButton from "./CSVLinkButton";
import usePermission from "utils/hooks/usePermission";
import Restricted from "components/common/restricted";
import { PERMISSIONS } from "permissions/app-permissions";
import TableTitle from "components/common/table-title/title";
import useOpenDateConfigs from "utils/hooks/useOpenDateConfigs";
import { isValidEnv, setEnv, setPendingEnv } from "redux/reducers/GlobalUiSlice";

const SRSApplications: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const role = TABLE_TYPE.SRS;
  const [allowed] = usePermission(PERMISSIONS.SRS_APPLICATION.RATE_STUDENTS);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<Array<string | undefined>>([]);
  const [defaultSorter, setDefaultSorter] = useState({});

  const {
    isLoading: isStudentTableDataLoading,
    isError: isStudentTableError,
    paginatedTableData,
    tableData,
    currentPage,
    filters,
    sorts,
    search,
    searchOrigin
  } = useAppSelector(state => state.srsApplicants);
  const { whiteLabel } = useAppSelector(state => state.properties);
  const { env, pendingEnv } = useAppSelector(state => state.globalUI);
  const isStudentEmpty = tableData.length == 0;
  const { ratedStudent } = useAppSelector(state => state.ratings);
  const { srsOpen } = useOpenDateConfigs();

  const pagination = {
    defaultCurrent: 1,
    total: tableData?.length,
    pageSize: studentTableSize,
    current: currentPage,
    responsive: true,
    showTotal: (total: any, range: any) => getTextForShowTotal(range[0], range[1], total)
  } as PaginationPropType;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFlagSubmitting, setIsFlagSubmitting] = useState<boolean>(false);
  const [HasFlgError, setHasFlagError] = useState<boolean>(false);
  const [isFlagSubmitted, setIsFlagSubmitted] = useState<boolean>(false);
  const [flagStudent, setFlagStudent] = useState<SRSApplicantType | undefined>(undefined);

  const showTableFooter = () => <TableFooter paginationProps={pagination} tableType={role} />;
  const locationState = history.location.state as any;

  useEffect(() => {
    if (whiteLabel) {
      if (!pendingEnv) {
        dispatch(fetchSRSApplicants({ source: env }));
      }
    }
  }, [env, pendingEnv, whiteLabel]);

  useEffect(() => {
    if (isValidEnv(pendingEnv) && env !== pendingEnv) {
      dispatch(setEnv(pendingEnv));
      dispatch(setPendingEnv(null));
    }
  }, [pathname, env, pendingEnv]);

  useEffect(() => {
    return () => dispatch(removeRatedStudent());
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    setIsFixed(window.innerWidth > 480 && window.innerWidth < 1200);
  }, [window.innerWidth]);

  useEffect(() => {
    if (!isStudentTableDataLoading && !isEmpty(locationState)) {
      if (locationState?.flagged) {
        setIsFlagSubmitted(true);
        setFlagStudent(locationState.flagStudent);
      }
      const criteriaObjWithRole = { ...locationState, role };
      const filterList = createStatusFilter(criteriaObjWithRole.filters);
      setStatusFilter(filterList);

      if (!isEmpty(criteriaObjWithRole.sorts)) {
        setDefaultSorter(criteriaObjWithRole.sorts);
      }

      dispatch(applyCriteria(criteriaObjWithRole));
      window.history.replaceState({}, document.title);
    }
  }, [isStudentTableDataLoading]);

  const actionTitle = () => <div style={{ textAlign: "right" }}>Actions</div>;

  // Modal: flag a student
  const cleanUpFlagStudent = () => {
    setFlagStudent(undefined);
    setIsFlagSubmitted(false);
    setHasFlagError(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmitFlag = async (refNum: number) => {
    setIsFlagSubmitting(true);
    setHasFlagError(false);
    setIsFlagSubmitted(false);
    try {
      const result = await api.post(ApiURL.flagStudent, { refNumber: refNum });
      if (result.data.status === API_RESPONSE_STATUS.SUCCESS) {
        setFlagStudent(undefined);
        const flagObj = {
          flagged: true,
          flagStudent
        };
        const criteriaObj = { ...flagObj, currentPage, filters, sorts, search, searchOrigin };
        history.push(`${AppURL.school.rater.srsApplications}`, criteriaObj);
        location.reload();
      } else {
        if (result.data.errors) {
          console.error(result.data.errors);
          setHasFlagError(true);
        }
      }
    } catch (error) {
      console.error(error);
      setHasFlagError(true);
    } finally {
      setIsFlagSubmitting(false);
      handleModalClose();
    }
  };

  // Table actions
  const searchByNameOrStudentId = (val: string) => {
    const originVal = val?.trim();
    const upperCaseVal = originVal?.toUpperCase();
    dispatch(searchTable({ target: upperCaseVal, targetOrigin: originVal }));
  };

  const handleChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: any
    ) => {
      if (Object.prototype.hasOwnProperty.call(sorter, "column")) {
        const sortDir =
          sorter.order == "ascend" ? "asc" : sorter.order == "descend" ? "desc" : undefined;
        const params = { field: sorter.field, dir: sortDir ?? undefined };

        if (!isEmpty(params)) {
          dispatch(sortTable({ sortObj: params, role }));
        }
      }
    },
    []
  );

  const applyOnClose = (visible: boolean, type: string, closeFilter?: any) => {
    if (!visible) {
      onFilter(type, closeFilter);
    }
  };

  const onFilter = (type: string, closeFilter?: any) => {
    const newFilters = updateFilters(filters, statusFilter, type);
    if (hasFilterChange(filters, newFilters)) {
      dispatch(filterTable({ newFilters, role }));
    }
    closeFilter && closeFilter();
  };

  const items: MenuProps["items"] = [
    {
      key: "flag-student",
      label: (
        <Button
          type="link"
          className={style["flag-link"]}
          onClick={() => setIsModalOpen(true)}
          aria-label={`Flag selected student ${flagStudent?.givenName1} ${flagStudent?.familyName}`}
          disabled={!allowed}
        >
          {t("srsApplications.flag.title")}
        </Button>
      )
    }
  ];

  const createRateButton = (student: any) => {
    const { fullName, schoolRated, refNum } = student;
    const hasBeenRated = schoolRated === SCHOOL_RATED.RATED;
    const isAbleToRate =
      schoolRated === SCHOOL_RATED.NOT_RATED || schoolRated === SCHOOL_RATED.PARTIAL;

    if (hasBeenRated) {
      return (
        <Space style={{ float: "right" }}>
          <span>
            <Tooltip title={t("srsApplications.ratedTooltip")}>
              <Button
                type="primary"
                disabled={true}
                icon={<CheckOutlined alt="" aria-hidden={true} />}
                aria-readonly="true"
                aria-label={`Rated student ${fullName}`}
                style={{ marginRight: "10px", marginTop: "5px" }}
              >
                {t("srsApplications.ratedBtn")}
              </Button>
            </Tooltip>
            <Restricted to={PERMISSIONS.SRS_APPLICATION.FLAG_BUTTON}>
              <Dropdown disabled={true}>
                <Button style={{ marginTop: "5px" }}>
                  <EllipsisOutlined />
                </Button>
              </Dropdown>
            </Restricted>
          </span>
        </Space>
      );
    } else if (isAbleToRate) {
      return (
        <Space style={{ float: "right" }}>
          <span>
            <Button
              type="primary"
              onClick={() => startRating(history, refNum.toString())}
              aria-label={`Rate selected student ${fullName}`}
              style={{ marginRight: "10px", marginTop: "5px", minWidth: "9.2rem" }}
              disabled={!allowed}
            >
              {t("srsApplications.unratedBtn")}
            </Button>
            <Restricted to={PERMISSIONS.SRS_APPLICATION.FLAG_BUTTON}>
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                onOpenChange={() => setFlagStudent(student)}
              >
                <Button style={{ marginTop: "5px" }} aria-label={`Flag unrecognised student`}>
                  <EllipsisOutlined />
                </Button>
              </Dropdown>
            </Restricted>
          </span>
        </Space>
      );
    } else {
      return <></>;
    }
  };

  const studentTableColumnsInRater: any | ColumnGroupType<any> | ColumnType<any> = [
    {
      key: "givenName1",
      dataIndex: "givenName1",
      title: ({ sortOrder }: any) => {
        return (
          <span>
            First name <AccessibleSortMsg sortOrder={sortOrder} />
          </span>
        );
      },
      csvTitle: "First name",
      defaultSortOrder: setSorter("givenName1", defaultSorter),
      sorter: true,
      render: formatName,
      showSorterTooltip: true
    },
    {
      key: "familyName",
      dataIndex: "familyName",
      title: ({ sortOrder }: any) => {
        return (
          <span>
            Family name <AccessibleSortMsg sortOrder={sortOrder} />
          </span>
        );
      },
      csvTitle: "Family name",
      defaultSortOrder: setSorter("familyName", defaultSorter),
      sorter: true,
      render: formatName,
      showSorterTooltip: true
    },
    {
      title: "Status",
      dataIndex: TABLE_FILTER.SCHOOL_RATED,
      key: TABLE_FILTER.SCHOOL_RATED,
      filterDropdown: ({ close: closeFilter }: any) => (
        <FilterBox
          option={schoolRatedOpt}
          handleChange={(checked: any) => setStatusFilter(checked)}
          values={statusFilter}
          clearFilter={() => setStatusFilter([])}
          onFilter={() => onFilter(TABLE_FILTER.SCHOOL_RATED, closeFilter)}
        />
      ),
      onFilterDropdownOpenChange: (visible: any) =>
        applyOnClose(visible, TABLE_FILTER.SCHOOL_RATED),
      render: (_: any, { schoolRated }: any): JSX.Element => {
        const obj = getStatusAndStyling(schoolRated);
        return <div className={obj.cssClass}>{obj.label}</div>;
      }
    },
    {
      title: ({ sortOrder }: any) => (
        <TableTitle
          sortOrder={sortOrder}
          title={t("students.studentIDTitle")}
          tooltipTitle={t("students.studentID")}
          altText={t("students.studentID")}
        />
      ),
      dataIndex: "studentNumber",
      key: "studentID",
      defaultSortOrder: setSorter("studentNumber", defaultSorter),
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: "Date of birth",
      dataIndex: "dob",
      key: "dob"
    },
    {
      title: "Submission date",
      dataIndex: "submissionDate",
      key: "submissionDate",
      defaultSortOrder: setSorter("submissionDate", defaultSorter),
      sorter: true,
      showSorterTooltip: true,
      render: (_: any, { submissionDate }: any): string => {
        const formattedTime = new Intl.DateTimeFormat("en-AU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          // interpret the db's timestamp without a timezone as sydney time
          timeZone: "Australia/Sydney"
        }).format(new Date(submissionDate));
        return formattedTime;
      }
    },
    {
      title: actionTitle,
      dataIndex: "schoolRated",
      key: "schoolRated",
      fixed: isFixed ? "right" : false,
      render: (_: any, student: SRSApplicantType): JSX.Element => {
        return createRateButton(student);
      }
    }
  ];

  const fullName = `${capitalize(ratedStudent.givenName1)} ${capitalize(ratedStudent.familyName)}`;

  return (
    <>
      <Helmet>
        <title>{t("helmet.srsApplications.title")}</title>
        <meta name="description" content={t("helmet.srsApplications.meta") || ""} />
      </Helmet>
      {!srsOpen ? (
        <div className="center-content" style={{ height: "80%" }}>
          <div style={{ width: "40rem" }}>
            <div className="center-justify">
              <img src={emptyBoxTransparent} alt="" />
            </div>
            <h1 className="text-center">{t("srsApplications.closed.title")}</h1>
            <p className="text-center">{t("srsApplications.closed.placeholder")}</p>
            <div className="center-justify">
              <Button type="primary" onClick={() => backToDashboard(history)}>
                {t("srsApplications.closed.backBtn")}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/*banner for rated student */}
          {ratedStudent?.fullName && (
            <Alert
              type="success"
              message={
                <strong style={{ fontWeight: 600 }}>
                  {t("srsApplications.ratedStudent.title", {
                    name: fullName
                  })}
                </strong>
              }
              description={t("srsApplications.ratedStudent.desc", {
                name: fullName
              })}
              afterClose={() => dispatch(removeRatedStudent())}
              style={{ marginBottom: "40px" }}
              closable
              showIcon
              icon={<img src={checkCircle} alt="" />}
            />
          )}

          {/*banner for flagged student*/}
          {isFlagSubmitted && !isEmpty(flagStudent) && (
            <Alert
              type="success"
              message={
                <strong style={{ fontWeight: 600 }}>
                  {`${formatName(
                    flagStudent?.fullName
                  )} has been successfully flagged as incorrectly linked to your school. `}
                </strong>
              }
              afterClose={cleanUpFlagStudent}
              style={{ marginBottom: "40px" }}
              closable
              showIcon
              icon={<img src={checkCircle} alt="" />}
            />
          )}

          {/*banner for flagged student error*/}
          {HasFlgError && (
            <Alert
              type="error"
              message={
                <strong style={{ fontWeight: 600 }}>
                  {`Something went wrong. Please try again. If the issue persists, please contact the portal administrator for assistance.`}
                </strong>
              }
              afterClose={cleanUpFlagStudent}
              style={{ marginBottom: "40px" }}
              closable
              showIcon
            />
          )}

          {isStudentTableError ? (
            <ErrorToDisplay title={t("loadingError")} />
          ) : isStudentTableDataLoading ? (
            <Spin size="large" className="center-content" style={{ height: "50vh" }} />
          ) : (
            <>
              <Row>
                <Col md={{ span: 20 }} sm={{ flex: "100%" }}>
                  <h1 className="display-mid-typo gap-section">{t("srsApplications.title")}</h1>
                </Col>
                <Col md={{ span: 4 }} sm={{ flex: "100%" }}>
                  <CSVLinkButton isStudentEmpty={isStudentEmpty} />
                </Col>
              </Row>

              <Search
                placeholder="Search by name or ID"
                defaultValue={locationState && locationState.searchOrigin}
                onSearch={searchByNameOrStudentId}
                style={{ maxWidth: 380, marginBottom: "2rem" }}
                aria-label="Search box for SRS applicants table"
                enterButton={<img src={iconSearch} alt="Search items in table data" />}
              />
              <Table
                rowKey={(record: SRSApplicantType) => `${record?.refNum}${record?.studentNumber}`}
                className="student-table"
                columns={studentTableColumnsInRater}
                locale={{
                  emptyText: (
                    <span>
                      <img src={emptyBox} alt="" />
                      <p>{t("srsApplications.empty")}</p>
                    </span>
                  )
                }}
                dataSource={paginatedTableData}
                footer={isStudentEmpty ? undefined : showTableFooter}
                pagination={false}
                loading={isStudentTableDataLoading}
                scroll={{ x: 992 }}
                onChange={handleChange}
              />
              {!isEmpty(flagStudent) && (
                <FlagModal
                  student={flagStudent}
                  isOpen={isModalOpen}
                  isSubmitting={isFlagSubmitting}
                  onClose={handleModalClose}
                  onSubmit={handleSubmitFlag}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default SRSApplications;
